"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { ArrowDropDownIcon, CartIcon, CloseIcon, HamburgerIcon, Input, InputIcon, Text, TextColor, TextSize, TextVariant, TextWeight, UserIcon } from "../../components";
import { AccountDropdown } from "./AccountDropdown";
import CarryLogo from "./carry-logo.jpg";
import { CartPreviewContent } from "./CartPreviewContent";

// Dane nawigacji wydzielone poza komponent
const MAIN_CATEGORIES = [{
  name: "Kobieta",
  url: "/odziez-damska"
}, {
  name: "Mężczyzna",
  url: "/odziez-meska"
}, {
  name: "Dziecko",
  url: "/odziez-dziecieca"
}];

// Stałe dla często używanych klas CSS
const COMMON_CLASSES = {
  ICON_WRAPPER: "flex flex-col gap-1 items-center tracking-widest",
  ICON_TEXT: "uppercase font-bold text-xxs text-black",
  LINK_PILL: "block border border-gray-200 rounded-full flex items-center justify-center py-2 px-6 w-fit hover:bg-gray-100"
};

// Styled components dla menu pionowego
const MenuContainer = styled.div`
  width: 100%;
  background-color: white;
`;
const AnimatedContainer = styled.div`
  max-height: ${props => props.$isExpanded ? "1000px" : "0"};
  overflow: hidden;
  transition: max-height 0.6s ease-in-out;
`;
const MainCategoryHeader = styled.div`
  background-color: black;
  color: white;
  display: flex;
  justify-content: space-between;
`;
const CategoryButton = styled.button`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border: none;
  background: none;
  border-bottom: 1px solid #e0e0e0;
  cursor: pointer;
  text-align: left;
`;
const SubcategoryContainer = styled.div`
  padding-left: 15px;
`;
const SubcategoryItem = styled.div`
  padding: 10px 15px;
  cursor: pointer;
  &:hover {
    background-color: #f5f5f5;
  }
`;
const TriangleIcon = styled.div`
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: ${props => props.$expanded ? "6px solid black" : "none"};
  border-bottom: ${props => !props.$expanded ? "6px solid black" : "none"};
`;

// Styled component dla wyszukiwarki
const StyledSearchWrapper = styled.div`
  ${({
  $isSearchActive
}) => $isSearchActive && `
    width: -webkit-fill-available;
    .MuiFormGroup-root {
      width: 70%;
    }
  `}

  .MuiFormGroup-root {
    transition: width 0.3s ease;
  }
`;

// Styled components dla wyników wyszukiwania
const SkeletonItem = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

// Komponent skeleton dla wyników wyszukiwania
const SearchResultsSkeleton = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mb-6"><SkeletonItem className="h-5 w-40 mb-3 py-4" />{Array.from({
        length: 5
      }).map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SkeletonItem className="h-5 w-40 mb-3 py-4" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="py-4">{t0}<div className="mb-4">{t1}<div className="flex flex-wrap gap-2">{Array.from({
            length: 4
          }).map(_temp2)}</div></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

// Komponent menu pionowego wykorzystujący MAIN_CATEGORIES
const VerticalDropdownMenu = t0 => {
  const $ = _c(32);
  const {
    menuItems,
    setIsVerticalMenuOpen
  } = t0;
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedSubcategory, setExpandedSubcategory] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      name: "Zaloguj si\u0119 lub zarejestruj si\u0119",
      url: "/logowanie"
    }, {
      name: "Lista salon\xF3w CARRY",
      url: "/lista-salonow-carry"
    }, {
      name: "Kontakt",
      url: "/kontakt"
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const footerLinks = t1;
  let t2;
  if ($[1] !== expandedCategory) {
    t2 = category => {
      if (expandedCategory === category) {
        setExpandedCategory(null);
      } else {
        setExpandedCategory(category);
      }
      setExpandedSubcategory(null);
    };
    $[1] = expandedCategory;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const toggleCategory = t2;
  let t3;
  if ($[3] !== expandedSubcategory) {
    t3 = subcategory => {
      if (expandedSubcategory === subcategory) {
        setExpandedSubcategory(null);
      } else {
        setExpandedSubcategory(subcategory);
      }
    };
    $[3] = expandedSubcategory;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const toggleSubcategory = t3;
  let t4;
  if ($[5] !== setIsVerticalMenuOpen) {
    t4 = MAIN_CATEGORIES.map(category_0 => <Link key={category_0.name} href={category_0.url} style={{
      flex: 1,
      textAlign: "center",
      padding: "12px 0"
    }} onClick={() => setIsVerticalMenuOpen(false)}><Text text={category_0.name.toUpperCase()} color={TextColor.WHITE} fontSize={TextSize.S} weight={TextWeight.BOLD} /></Link>);
    $[5] = setIsVerticalMenuOpen;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <MainCategoryHeader>{t4}</MainCategoryHeader>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <CategoryButton><Text text={"NOWO\u015ACI"} color={TextColor.BLACK} fontSize={TextSize.S} weight={TextWeight.BOLD} /></CategoryButton>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <CategoryButton><Text text="PROMOCJE" color={TextColor.BLACK} fontSize={TextSize.S} weight={TextWeight.BOLD} /></CategoryButton>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== expandedCategory || $[12] !== expandedSubcategory || $[13] !== menuItems || $[14] !== setIsVerticalMenuOpen || $[15] !== toggleCategory || $[16] !== toggleSubcategory) {
    let t9;
    if ($[18] !== expandedCategory || $[19] !== expandedSubcategory || $[20] !== setIsVerticalMenuOpen || $[21] !== toggleCategory || $[22] !== toggleSubcategory) {
      t9 = category_1 => <div key={`dropdown-${category_1.name}`}><CategoryButton onClick={() => toggleCategory(category_1.name)}><Text text={category_1.name.toUpperCase()} color={TextColor.BLACK} fontSize={TextSize.XS} weight={TextWeight.BOLD} /><TriangleIcon $expanded={expandedCategory === category_1.name} /></CategoryButton><AnimatedContainer $isExpanded={expandedCategory === category_1.name}><div><CategoryButton style={{
              paddingLeft: "30px"
            }} onClick={() => setIsVerticalMenuOpen(false)}><Link href={category_1.url}><Text text="ZOBACZ WSZYSTKO" color={TextColor.BLACK} fontSize={TextSize.S} weight={TextWeight.NORMAL} /></Link></CategoryButton><CategoryButton style={{
              paddingLeft: "30px"
            }}><Text text={"NOWO\u015ACI"} color={TextColor.BLACK} fontSize={TextSize.S} weight={TextWeight.NORMAL} /></CategoryButton>{category_1.children.map(child => <div key={child.id}><CategoryButton style={{
                paddingLeft: "30px"
              }} onClick={e => {
                e.stopPropagation();
                toggleSubcategory(`${category_1.name}-${child.name}`);
              }}><Text text={child.name.toUpperCase()} color={TextColor.BLACK} fontSize={TextSize.S} weight={TextWeight.NORMAL} /><TriangleIcon $expanded={expandedSubcategory === `${category_1.name}-${child.name}`} /></CategoryButton><AnimatedContainer $isExpanded={expandedSubcategory === `${category_1.name}-${child.name}`}><SubcategoryContainer><SubcategoryItem onClick={() => setIsVerticalMenuOpen(false)}><Link href={child.url}><Text text="Zobacz wszystko" color={TextColor.GRAYSCALE_700} fontSize={TextSize.S} /></Link></SubcategoryItem>{child.children?.map((item, itemIndex) => <SubcategoryItem key={`${child.name}-item-${itemIndex}`} onClick={() => setIsVerticalMenuOpen(false)}><Link href={item.url}><Text text={item.name} color={TextColor.GRAYSCALE_700} fontSize={TextSize.S} /></Link></SubcategoryItem>)}</SubcategoryContainer></AnimatedContainer></div>)}</div></AnimatedContainer></div>;
      $[18] = expandedCategory;
      $[19] = expandedSubcategory;
      $[20] = setIsVerticalMenuOpen;
      $[21] = toggleCategory;
      $[22] = toggleSubcategory;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t8 = menuItems.map(t9);
    $[11] = expandedCategory;
    $[12] = expandedSubcategory;
    $[13] = menuItems;
    $[14] = setIsVerticalMenuOpen;
    $[15] = toggleCategory;
    $[16] = toggleSubcategory;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[24] !== setIsVerticalMenuOpen) {
    t9 = <div>{footerLinks.map((link, index) => <CategoryButton key={`footer-link-${index}`} onClick={() => setIsVerticalMenuOpen(false)}><Link href={link.url}><Text text={link.name} color={TextColor.GRAYSCALE_700} fontSize={TextSize.S} /></Link></CategoryButton>)}</div>;
    $[24] = setIsVerticalMenuOpen;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t8 || $[27] !== t9) {
    t10 = <div className="px-2">{t6}{t7}{t8}{t9}</div>;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t5) {
    t11 = <MenuContainer>{t5}{t10}</MenuContainer>;
    $[29] = t10;
    $[30] = t5;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
};

// Główny komponent nawigacji
export function NavbarClient(t0) {
  const $ = _c(79);
  const {
    isLoggedIn,
    userName,
    logout,
    isLoadingCart,
    isLoadingAccount,
    cart,
    menuItems,
    popularSearches: t1,
    matchingCategories: t2,
    loadingPopularSearches,
    getPopularSearchesData,
    clearPopularSearchesData
  } = t0;
  let t3;
  if ($[0] !== t1) {
    t3 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const popularSearches = t3;
  let t4;
  if ($[2] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const matchingCategories = t4;
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [isMobileMenuOpen] = useState(false);
  const [isVerticalMenuOpen, setIsVerticalMenuOpen] = useState(false);
  const verticalMenuRef = useRef(null);
  const router = useRouter();
  let t5;
  let t6;
  if ($[4] !== getPopularSearchesData || $[5] !== isSearchActive || $[6] !== searchValue) {
    t5 = () => {
      if (searchValue.length >= 3) {
        const timeoutId = setTimeout(() => {
          getPopularSearchesData(searchValue);
        }, 300);
        return () => clearTimeout(timeoutId);
      } else {
        if (searchValue.length === 0 && isSearchActive) {
          const timeoutId_0 = setTimeout(() => {
            getPopularSearchesData("");
          }, 100);
          return () => clearTimeout(timeoutId_0);
        }
      }
    };
    t6 = [searchValue, getPopularSearchesData, isSearchActive];
    $[4] = getPopularSearchesData;
    $[5] = isSearchActive;
    $[6] = searchValue;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== getPopularSearchesData) {
    t7 = () => {
      setIsSearchActive(true);
      getPopularSearchesData("");
    };
    $[9] = getPopularSearchesData;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const handleSearchInputOpen = t7;
  let t8;
  if ($[11] !== clearPopularSearchesData) {
    t8 = () => {
      setIsSearchActive(false);
      setSearchValue("");
      clearPopularSearchesData();
    };
    $[11] = clearPopularSearchesData;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleSearchInputClose = t8;
  let t9;
  if ($[13] !== handleSearchInputClose || $[14] !== router) {
    t9 = searchTerm => {
      router.push(`/szukaj?s=${encodeURIComponent(searchTerm)}`);
      handleSearchInputClose();
    };
    $[13] = handleSearchInputClose;
    $[14] = router;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleSearchSubmit = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsVerticalMenuOpen(_temp3);
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const toggleVerticalMenu = t10;
  let t11;
  if ($[17] !== isMobileMenuOpen || $[18] !== menuItems) {
    t11 = () => <div className={`absolute left-0 top-full border-t w-full bg-white border-b transition-all duration-200 z-10 ${isMobileMenuOpen ? "visible opacity-100" : "invisible opacity-0"}`}><div className="grid grid-cols-3 divide-x-3 w-full">{menuItems.map(_temp4)}</div></div>;
    $[17] = isMobileMenuOpen;
    $[18] = menuItems;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const renderMobileMenu = t11;
  let t12;
  if ($[20] !== menuItems) {
    t12 = () => <div className="hidden lg:flex items-center justify-start gap-8">{menuItems.map(_temp5)}</div>;
    $[20] = menuItems;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const renderDesktopNavigation = t12;
  let t13;
  if ($[22] !== handleSearchInputClose || $[23] !== loadingPopularSearches || $[24] !== matchingCategories || $[25] !== popularSearches || $[26] !== router || $[27] !== searchValue.length) {
    t13 = () => <div className="absolute left-0 top-full py-4 w-full max-h-[500px] overflow-y-auto bg-white border-b transition-all duration-200 z-10 2xl:px-0 px-4"><div className="flex flex-col">{loadingPopularSearches ? <SearchResultsSkeleton /> : <>{matchingCategories.length > 0 && <div className="mb-6"><Text text="Kategorie" variant={TextVariant.H6} weight={TextWeight.BOLD} textTransform="uppercase" className="py-4" />{matchingCategories.map(category_0 => <div key={category_0.idCategory} onClick={() => {
              router.push(category_0.categoryPath);
              handleSearchInputClose();
            }} className="flex gap-2 items-center cursor-pointer"><Text text={category_0.name} color={TextColor.GRAYSCALE_700} className="py-1" /><Text text={category_0.parentName} color={TextColor.GRAYSCALE_400} fontSize={TextSize.XS} className="ml-2" /></div>)}</div>}{popularSearches.length > 0 && <div className="mb-6"><Text text={"Cz\u0119sto wyszukiwane"} variant={TextVariant.H6} weight={TextWeight.BOLD} textTransform="uppercase" className="py-4" /><div className="flex gap-2">{popularSearches.map((search, index) => <Link key={index} href={`/szukaj?s=${encodeURIComponent(search.keywords)}`} className={COMMON_CLASSES.LINK_PILL} onClick={() => handleSearchInputClose()}><Text text={search.keywords} color={TextColor.GRAYSCALE_700} /></Link>)}</div></div>}{searchValue.length >= 3 && popularSearches.length === 0 && matchingCategories.length === 0 && <div className="py-4 text-center"><Text text={"Brak wynik\xF3w wyszukiwania"} color={TextColor.GRAYSCALE_500} fontSize={TextSize.S} /></div>}</>}</div></div>;
    $[22] = handleSearchInputClose;
    $[23] = loadingPopularSearches;
    $[24] = matchingCategories;
    $[25] = popularSearches;
    $[26] = router;
    $[27] = searchValue.length;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const renderSearchResults = t13;
  const t14 = `${isSearchActive ? "flex justify-between" : "grid grid-cols-[auto_1fr_auto_auto_auto]"} items-center h-[75px]`;
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="lg:hidden flex flex-col gap-1 items-center tracking-widest mr-4 py-4 cursor-pointer" onClick={toggleVerticalMenu}><HamburgerIcon width={20} height={20} /><Text text="Menu" color={TextColor.BLACK} fontSize={TextSize.XXS} weight={TextWeight.BOLD} textTransform="uppercase" /></div>;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== isVerticalMenuOpen || $[31] !== menuItems) {
    t16 = isVerticalMenuOpen && <div className="absolute left-0 top-full z-20 w-full" ref={verticalMenuRef}><VerticalDropdownMenu menuItems={menuItems} setIsVerticalMenuOpen={setIsVerticalMenuOpen} /></div>;
    $[30] = isVerticalMenuOpen;
    $[31] = menuItems;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== isSearchActive) {
    t17 = !isSearchActive && <Link href="/" className="flex mr-8"><Image src={CarryLogo} alt="Carry Logo" width={90} height={32} /></Link>;
    $[33] = isSearchActive;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== isSearchActive || $[36] !== renderDesktopNavigation || $[37] !== renderMobileMenu) {
    t18 = !isSearchActive && <div className="flex w-full justify-between items-center">{renderMobileMenu()}{renderDesktopNavigation()}</div>;
    $[35] = isSearchActive;
    $[36] = renderDesktopNavigation;
    $[37] = renderMobileMenu;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = e => setSearchValue(e.target.value);
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== handleSearchInputClose || $[41] !== handleSearchInputOpen || $[42] !== handleSearchSubmit || $[43] !== isSearchActive || $[44] !== searchValue) {
    t20 = e_0 => {
      if (e_0.key === "Escape") {
        handleSearchInputClose();
      } else {
        if (e_0.key === "Enter" && searchValue.trim()) {
          handleSearchSubmit(searchValue.trim());
        } else {
          if (!isSearchActive) {
            handleSearchInputOpen();
          }
        }
      }
    };
    $[40] = handleSearchInputClose;
    $[41] = handleSearchInputOpen;
    $[42] = handleSearchSubmit;
    $[43] = isSearchActive;
    $[44] = searchValue;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== handleSearchInputOpen || $[47] !== searchValue || $[48] !== t20) {
    t21 = <Input id="search" name="search" variant="backgroundOnly" placeholder="Szukaj" icon={true} iconPosition="left" iconVariant={InputIcon.SEARCH} value={searchValue} onChange={t19} onKeyDown={t20} onFocus={handleSearchInputOpen} onClick={handleSearchInputOpen} />;
    $[46] = handleSearchInputOpen;
    $[47] = searchValue;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== handleSearchInputClose || $[51] !== isSearchActive) {
    t22 = isSearchActive && <div className="cursor-pointer" onClick={handleSearchInputClose}><CloseIcon width={20} height={20} /></div>;
    $[50] = handleSearchInputClose;
    $[51] = isSearchActive;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== isSearchActive || $[54] !== t21 || $[55] !== t22) {
    t23 = <StyledSearchWrapper className="hidden md:flex items-center gap-2 search-input h-[75px] pr-8" $isSearchActive={isSearchActive}>{t21}{t22}</StyledSearchWrapper>;
    $[53] = isSearchActive;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== isSearchActive || $[58] !== renderSearchResults) {
    t24 = isSearchActive && renderSearchResults();
    $[57] = isSearchActive;
    $[58] = renderSearchResults;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== cart || $[61] !== isLoadingCart) {
    t25 = isLoadingCart ? <div className="h-[40px] w-[50px] rounded bg-muted animate-pulse" /> : <CartPreviewContent icon={<CartIcon width={20} height={20} />} cart={cart} />;
    $[60] = cart;
    $[61] = isLoadingCart;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== isLoadingAccount || $[64] !== isLoggedIn || $[65] !== logout || $[66] !== userName) {
    t26 = isLoadingAccount ? <div className="h-[40px] w-[50px] rounded bg-muted animate-pulse" /> : <AccountDropdown icon={<UserIcon width={20} height={20} />} isLoggedIn={isLoggedIn} userName={userName} logout={logout} />;
    $[63] = isLoadingAccount;
    $[64] = isLoggedIn;
    $[65] = logout;
    $[66] = userName;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] !== t25 || $[69] !== t26) {
    t27 = <div className="flex justify-end items-center gap-8">{t25}{t26}</div>;
    $[68] = t25;
    $[69] = t26;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t14 || $[72] !== t16 || $[73] !== t17 || $[74] !== t18 || $[75] !== t23 || $[76] !== t24 || $[77] !== t27) {
    t28 = <nav className={t14}>{t15}{t16}{t17}{t18}{t23}{t24}{t27}</nav>;
    $[71] = t14;
    $[72] = t16;
    $[73] = t17;
    $[74] = t18;
    $[75] = t23;
    $[76] = t24;
    $[77] = t27;
    $[78] = t28;
  } else {
    t28 = $[78];
  }
  return t28;
}

// Komponenty wydzielone dla lepszej organizacji kodu
function _temp5(menuItem) {
  return <CategoryDropdown key={`category-dropdown-${menuItem.name}_${Math.random()}`} menuItem={menuItem} />;
}
function _temp4(category) {
  return <div key={category.name} className="bg-black flex justify-center items-center border-r border-white"><Link href={`/category/${category.name.toLowerCase()}`}><Text text={category.name} color={TextColor.WHITE} fontSize={TextSize.XS} weight={TextWeight.BOLD} textTransform="uppercase" className="py-4" /></Link></div>;
}
function _temp3(prev) {
  return !prev;
}
function CategoryDropdown(t0) {
  const $ = _c(15);
  const {
    menuItem
  } = t0;
  let t1;
  if ($[0] !== menuItem.name) {
    t1 = <Text text={menuItem.name} weight={TextWeight.BOLD} textTransform="uppercase" className="tracking-widest" color={TextColor.BLACK} fontSize={TextSize.XS} />;
    $[0] = menuItem.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ArrowDropDownIcon className="transition-transform group-hover:rotate-180" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <button className="flex items-center py-4 h-[75px] text-gray-700 hover:text-gray-900 focus:outline-none">{t1}{t2}</button>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== menuItem.children || $[6] !== menuItem.name) {
    let t5;
    if ($[8] !== menuItem.name) {
      t5 = (child, index) => <StyledSubMenuContainer $isHeaderLink={child.url !== "/"} key={`${menuItem.name}-${child.name}-${index}-${Math.random()}`} className="w-1/6">{child.url !== "/" ? <Link href={child.url}><Text text={child.name} variant={TextVariant.H6} weight={TextWeight.BOLD} textTransform="uppercase" className="py-4" /></Link> : <Text text={child.name} variant={TextVariant.H6} weight={TextWeight.BOLD} textTransform="uppercase" className="py-4" />}{child.children?.map(subsubcategory => <Link key={`${menuItem.name}-${child.name}-${subsubcategory.name}-${index}-${Math.random()}`} href={subsubcategory.url} className="block"><Text text={subsubcategory.name} color={TextColor.GRAYSCALE_700} fontSize={TextSize.S} className="py-1" /></Link>)}<Link href={child.url} className="block"><Text text="Zobacz wszystkie" color={TextColor.BLACK} fontSize={TextSize.S} weight={TextWeight.SEMIBOLD} textDecoration="underline" className="py-4" /></Link></StyledSubMenuContainer>;
      $[8] = menuItem.name;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    t4 = menuItem.children.map(t5);
    $[5] = menuItem.children;
    $[6] = menuItem.name;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <StyledDropdown className="absolute left-0 top-full pb-4 w-full border-b opacity-0 group-hover:opacity-100 group-hover:visible invisible transition-all duration-200 z-10 2xl:px-0 px-4"><div className="flex flex-row">{t4}</div></StyledDropdown>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t5) {
    t6 = <div className="group">{t3}{t5}</div>;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}

// Komponenty Skeleton wydzielone do osobnego pliku dla lepszej organizacji
export function NavbarSkeletonResponsive() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="hidden md:flex space-x-8 items-center"><div className="h-[32px] w-[90px] rounded bg-muted animate-pulse" />{Array.from({
        length: 4
      }).map(_temp6)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="hidden md:block h-10 w-[220px] rounded-md bg-muted animate-pulse" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex justify-between items-center w-full">{t0}<div className="flex items-center gap-8">{t1}<div className="flex space-x-4">{Array.from({
            length: 2
          }).map(_temp7)}</div><div className="md:hidden h-10 w-10 rounded-md bg-muted animate-pulse" /></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp7(__0, index_0) {
  return <div key={`nav-button-skeleton-${index_0}`} className="h-10 w-14 rounded-md bg-muted animate-pulse" />;
}
function _temp6(_, index) {
  return <div key={`nav-item-skeleton-${index}`} className="h-6 w-24 rounded bg-muted animate-pulse" />;
}
const StyledSubMenuContainer = styled.div`
  ${({
  $isHeaderLink,
  theme
}) => $isHeaderLink && `
      h6 {
    &:hover {
      color: ${theme.colors.GRAYSCALE_900};
      text-decoration: underline;
    }
  }
  `}
`;
const StyledDropdown = styled.div`
  background-color: ${({
  theme
}) => theme.backgrounds.theme};

  p {
    &:hover {
      color: ${({
  theme
}) => theme.colors.GRAYSCALE_900};
      text-decoration: underline;
    }
  }
`;
function _temp(_, index) {
  return <div key={`category-skeleton-${index}`} className="flex gap-2 items-center mb-4"><SkeletonItem className="h-6 w-32" /><SkeletonItem className="h-4 w-24" /></div>;
}
function _temp2(__0, index_0) {
  return <SkeletonItem key={`popular-skeleton-${index_0}`} className="h-8 rounded-full w-40" />;
}